import axios, { AxiosRequestConfig } from "axios";
import { TOKEN_KEYS } from "../auth";
import type { SSOConfig } from "./types";
import { getOriginalEncodedUrl } from "./utils";

/**
 * Generates a random string using crypto API for PKCE code verifier.
 * @returns A random string (112 hex characters from 28 32-bit values)
 */
export function generateRandomString(): string {
  const array = new Uint32Array(28);
  globalThis.crypto.getRandomValues(array);
  return Array.from(array, (val: number) =>
    `0${val.toString(16)}`.slice(-2),
  ).join("");
}

/**
 * Generates code challenge from the given verifier using SHA-256.
 * @param verifier The code verifier
 * @returns The code challenge (base64url-encoded SHA-256 hash)
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  const arrayFromUint8 = Array.from(new Uint8Array(digest));
  return btoa(String.fromCharCode(...arrayFromUint8))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

/**
 * Initiates the PKCE OAuth flow by redirecting to the authorization URL.
 * Stores code verifier and state in sessionStorage before redirect.
 * @param config SSO configuration with clientId, authUrl, and optional redirectUri
 */
export async function startPKCEFlow(config: SSOConfig): Promise<void> {
  const {
    clientId,
    authUrl,
    redirectUri = `${globalThis.location.origin}/callback`,
  } = config;

  const codeVerifier = generateRandomString();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = getOriginalEncodedUrl();

  sessionStorage.setItem(TOKEN_KEYS.CODE_VERIFIER, codeVerifier);
  sessionStorage.setItem("oauth_state", state);

  globalThis.location.href = `${authUrl}?response_type=code&client_id=${clientId}&code_challenge=${codeChallenge}&code_challenge_method=S256&redirect_uri=${redirectUri}&state=${state}`;
}

/**
 * Refreshes the access token using the provided refresh token.
 * Stores the new tokens in sessionStorage.
 * @param refreshToken The refresh token
 * @param config SSO configuration with clientId and accessTokenUrl
 * @throws Error if refresh fails
 */
export async function refreshAccessToken(
  refreshToken: string,
  config: SSOConfig,
): Promise<void> {
  const { clientId, accessTokenUrl } = config;
  const state = getOriginalEncodedUrl();

  sessionStorage.setItem("oauth_state", state);

  const payload = {
    client_id: clientId,
    grant_type: "refresh_token",
    refresh_token: refreshToken,
  };

  const formData = new URLSearchParams(payload).toString();

  const axiosConfig: AxiosRequestConfig = {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  };

  try {
    const response = await axios.post(accessTokenUrl, formData, axiosConfig);

    if (response?.data?.access_token) {
      const accessToken = response.data.access_token || "";
      const idToken = response.data.id_token || "";
      const expiresIn = response.data.expires_in || 3600;
      const expiresAt = Date.now() + expiresIn * 1000;

      sessionStorage.setItem(TOKEN_KEYS.ACCESS, accessToken);
      sessionStorage.setItem(TOKEN_KEYS.ID, idToken);
      sessionStorage.setItem(TOKEN_KEYS.EXPIRY, expiresAt.toString());
    } else {
      throw new Error(
        response?.data?.message || "Error refreshing access token",
      );
    }
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    throw new Error(
      err?.response?.data?.message || "Error refreshing access token",
    );
  }
}

/**
 * Exchanges the authorization code for tokens.
 * Stores the tokens in sessionStorage and clears the code verifier.
 * @param code The authorization code from OAuth callback
 * @param config SSO configuration with clientId, accessTokenUrl, and optional redirectUri
 * @throws Error if code verifier is missing or exchange fails
 */
export async function exchangeCodeForToken(
  code: string,
  config: SSOConfig,
): Promise<void> {
  const {
    clientId,
    accessTokenUrl,
    redirectUri = `${globalThis.location.origin}/callback`,
  } = config;

  const codeVerifier = sessionStorage.getItem(TOKEN_KEYS.CODE_VERIFIER);

  if (!codeVerifier) {
    throw new Error("Code verifier is missing.");
  }

  const payload = {
    client_id: clientId,
    grant_type: "authorization_code",
    code,
    code_verifier: codeVerifier,
    redirect_uri: redirectUri,
  };

  const formData = new URLSearchParams(payload).toString();

  const axiosConfig: AxiosRequestConfig = {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  };

  try {
    const response = await axios.post(accessTokenUrl, formData, axiosConfig);

    if (response?.data?.access_token) {
      const accessToken = response.data.access_token || "";
      const idToken = response.data.id_token || "";
      const expiresIn = response.data.expires_in || 3600;
      const refreshToken = response.data.refresh_token || "";
      const expiresAt = Date.now() + expiresIn * 1000;

      sessionStorage.setItem(TOKEN_KEYS.ACCESS, accessToken);
      sessionStorage.setItem(TOKEN_KEYS.ID, idToken);
      sessionStorage.setItem(TOKEN_KEYS.REFRESH, refreshToken);
      sessionStorage.setItem(TOKEN_KEYS.EXPIRY, expiresAt.toString());
      sessionStorage.setItem(TOKEN_KEYS.USER_ENCODED_NTID, btoa(clientId));
      sessionStorage.removeItem(TOKEN_KEYS.CODE_VERIFIER);
    } else {
      throw new Error(
        response?.data?.message || "Error exchanging code for token",
      );
    }
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    throw new Error(
      err?.response?.data?.message || "Error exchanging code for token",
    );
  }
}
